import { fetchApi, fetchRest, getApiBaseUrl } from '@/config/api';

export interface ReplacementProduct {
  id_produit: number;
  reference: string;
  couleur: string;
  id_categorie?: number | null;
  image_url?: string;
  prix_unitaire_ht: number;
  vendu_par: string;
  qte_mini: number;
  tailles: string[];
  pack: number[];
  stock: Array<{ taille: string | null; value: number }>;
}
export interface ReplacementSnapshot {
  vendu_par?: string;
  nom_boutique?: string;
  montant_facture_ht?: number;
  charge_vendeur_ht?: number;
  charge_vendeur_ttc?: number;
  a_rembourser_ht?: number;
  a_rembourser_ttc?: number;
  prix_corbeille_apres?: number;
  id_produit: number;
  reference: string;
  couleur: string;
  image_url?: string;
  quantites: number[];
  tailles: string[];
  quantite_pack: number;
  montant_brut_ht: number;
  montant_ht: number;
  montant_ttc: number;
}
export interface ReplacementLine {
  date_creation?: string;
  date_expiration?: string;
  charge_vendeur_max_ttc?: number;
  statut?: 'pending' | 'accepted' | 'rejected' | 'expired' | 'cancelled' | 'confirmer';
  nom_boutique?: string;
  id_commande?: number;
  id_commande_groupe?: number;
  id_vendeur?: number;
  id_ligne_origine: number;
  origine: ReplacementSnapshot;
  accepted?: 0 | 1;
  rejected?: 0 | 1;
  remplacement: ReplacementSnapshot | ReplacementAppliedProduct[];
  montant_facture_brut_ht: number;
  charge_vendeur_ttc: number;
  a_rembourser_ttc: number;
  id_ligne_remplacement: number | null;
}
export interface ReplacementAppliedProduct extends ReplacementSnapshot {
  montant_facture_brut_ht: number;
  id_ligne_remplacement: number | null;
}
export function replacementLineProducts(line: ReplacementLine): ReplacementAppliedProduct[] {
  return Array.isArray(line.remplacement) ? line.remplacement : [{ ...line.remplacement, montant_facture_brut_ht: line.montant_facture_brut_ht, id_ligne_remplacement: line.id_ligne_remplacement }];
}
export interface ReplacementSelectedProduct extends ReplacementSnapshot {
  id_ligne_origine: number;
  id_produit_origine: number;
  id_ligne_remplacement: number | null;
}
export interface ReplacementProposal {
  confirmation_remplacements?: boolean;
  scope?: 'commande_groupe';
  id_vendeur?: number;
  commandes?: ReplacementProposal[];
  id_jet: string;
  id_commande: number;
  id_commande_groupe: number;
  reference_commande?: string;
  devise?: 'EUR';
  peut_repondre?: boolean;
  montants_provisoires?: boolean;
  totaux?: {
    a_rembourser_ht: number;
    a_rembourser_ttc: number;
    charge_vendeur_ht: number;
    charge_vendeur_ttc: number;
    supplement_acheteur_ttc: number;
  };
  statut: 'pending' | 'accepted' | 'rejected' | 'expired' | 'cancelled' | 'confirmer';
  version: number;
  date_creation: string;
  date_expiration: string;
  date_reponse: string | null;
  date_application: string | null;
  lignes: ReplacementLine[];
  produit_remplacement?: ReplacementSelectedProduct[];
  tva_rate: number;
  remise_fixe_commande_ht?: number;
  notification: string;
}
export interface ReplacementSelection {
  id_ligne_origine: number;
  id_produit_remplacement: number;
  quantites: number[];
  quantite_pack: number;
}
export interface ReplacementInput {
  id_commande: number;
  lignes: ReplacementSelection[];
  stock_confirme: boolean;
  quote_hash?: string;
}
export interface ReplacementLink { id_jet: string; url?: string; notification: string; lien_conserve?: boolean }

export function replacementProductTotal(product: ReplacementProduct, selection?: ReplacementSelection) {
  const selected = selection?.id_produit_remplacement === product.id_produit ? selection : undefined;
  const quantity = selected ? selected.quantites.reduce((sum, q) => sum + (Number(q) || 0), 0)
    : product.vendu_par === 'tailles' ? Math.max(1, product.qte_mini)
    : product.pack.reduce((sum, q) => sum + q, 0);
  const amount = quantity > 0 ? Math.round((product.prix_unitaire_ht * quantity + Number.EPSILON) * 100) / 100 : null;
  const description = product.vendu_par === 'tailles' ? `${selected ? '' : 'Minimum : '}${quantity} pièce(s)`
    : product.vendu_par === 'seul' ? `${quantity} pièce(s)`
    : selected ? `${quantity} pièce(s) sélectionnée(s)` : `1 paquet · ${quantity} pièce(s)`;
  return { amount, description };
}

async function graphqlJson<T>(query: string, variables: Record<string, unknown>, field: string): Promise<T> {
  const data = await fetchApi<Record<string, { json: string }>>(query, variables);
  return JSON.parse(data[field].json) as T;
}
async function adminReplacementJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetchRest(`${getApiBaseUrl()}/api/remplacements/admin${path}`, init);
  const data = await response.json();
  if (!response.ok) {
    const message = Array.isArray(data.message) ? data.message.join(' · ') : data.message;
    throw new Error(typeof message === 'string' ? message : 'Impossible de modifier la recommandation');
  }
  return data as T;
}
export function fetchReplacementProducts(id: number, search: string, lineId?: number, admin = false) {
  if (admin) {
    const params = new URLSearchParams({ recherche: search });
    if (lineId != null) params.set('id_ligne_origine', String(lineId));
    return adminReplacementJson<ReplacementProduct[]>(`/commandes/${id}/produits?${params}`);
  }
  return graphqlJson<ReplacementProduct[]>(`query($id: Int!, $search: String!, $lineId: Int) {
    remplacementProduits(idCommande: $id, recherche: $search, idLigneOrigine: $lineId) { json }
  }`, { id, search, lineId }, 'remplacementProduits');
}
export function fetchOrderReplacements(id: number, admin = false) {
  if (admin) return adminReplacementJson<ReplacementProposal[]>(`/commandes/${id}`);
  return graphqlJson<ReplacementProposal[]>(`query($id: Int!) {
    remplacementsCommande(idCommande: $id) { json }
  }`, { id }, 'remplacementsCommande');
}
export interface AdminReplacementSummary {
  total: number;
  pending: number;
  accepted: number;
  a_rembourser_ttc: number;
  charge_vendeur_ttc: number;
  charge_vendeur_en_attente_max_ttc: number | null;
  montants_provisoires: boolean;
  donnees_incompletes: boolean;
}
export interface AdminReplacementRecord {
  id: string;
  id_jet: string;
  id_commande: number;
  version: number;
  date_creation: string;
  date_expiration: string;
  date_application_proposition: string | null;
  remboursement_effectue: boolean;
  remboursement_en_cours: boolean;
  remboursement_session: string | null;
  ligne: ReplacementLine;
  montants: {
    montant_origine_brut_ht: number | null;
    montant_remplacement_brut_ht: number | null;
    ecart_brut_ht: number | null;
    a_rembourser_ttc: number | null;
    charge_vendeur_ttc: number | null;
    charge_vendeur_en_attente_max_ttc: number | null;
    montants_provisoires: boolean;
  };
}
interface AdminReplacementHistorySnapshot {
  id_ligne_origine: number;
  origine: { reference: string; couleur: string };
  produits: Array<Pick<ReplacementSnapshot, 'id_produit' | 'reference' | 'couleur' | 'tailles' | 'quantites' | 'quantite_pack' | 'montant_brut_ht'>>;
}
export interface AdminReplacementHistory {
  id_commande: number;
  id_commande_groupe: number;
  reference_commande: string;
  remboursement_production: boolean;
  resume: AdminReplacementSummary;
  lignes: AdminReplacementRecord[];
  historique: Array<{
    id: string;
    action: string;
    acteur: string;
    date: string;
    version: number | null;
    ids_lignes: number[];
    avant: AdminReplacementHistorySnapshot[];
    apres: AdminReplacementHistorySnapshot[];
  }>;
  historique_limite: boolean;
}
export function fetchAdminReplacementHistory(id: number) {
  return adminReplacementJson<AdminReplacementHistory>(`/commandes/${id}/historique`);
}
export async function refundAdminReplacement(input: {
  idCommandeGroupe: number;
  total: number;
  remplacement: { idCommande: number; idJet: string; idLigne: number };
  refundSessionId?: string;
}): Promise<{ msg: boolean; pending?: boolean; refundSessionId?: string }> {
  const response = await fetchRest(`${getApiBaseUrl()}/api/admin-efp/commandes/remplacements/refund`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input), credentials: 'include',
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(result.message || result.error || 'Échec du remboursement');
  return result;
}
export function previewReplacement(input: ReplacementInput, admin = false) {
  if (admin) return adminReplacementJson<{ lignes: ReplacementLine[]; tva_rate: number; quote_hash: string; remise_fixe_commande_ht?: number }>(`/commandes/${input.id_commande}/apercu`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lignes: input.lignes, stock_confirme: input.stock_confirme }),
  });
  return graphqlJson<{ lignes: ReplacementLine[]; tva_rate: number; quote_hash: string; remise_fixe_commande_ht?: number }>(`mutation($input: ProposerRemplacementInput!) {
    apercuRemplacement(input: $input) { json }
  }`, { input }, 'apercuRemplacement');
}
export function proposeReplacement(input: ReplacementInput) {
  return graphqlJson<ReplacementLink>(`mutation($input: ProposerRemplacementInput!) {
    proposerRemplacement(input: $input) { json }
  }`, { input }, 'proposerRemplacement');
}
export interface ReplacementCandidateTarget {
  id_jet: string; id_ligne_origine: number; id_produit: number;
}
export interface ReplacementCandidateContext { id_commande: number; version: number }
export interface ReplacementCandidateQuantity extends ReplacementCandidateContext {
  quantites: number[]; quantite_pack: number; stock_confirme: boolean; quote_hash: string;
}
function candidateOperation(target: ReplacementCandidateTarget, operation: string, body: object) {
  const path = `/${encodeURIComponent(target.id_jet)}/lignes/${target.id_ligne_origine}/produits/${target.id_produit}/${operation}`;
  return adminReplacementJson<ReplacementProposal>(path, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
}
export function updateReplacementQuantity(target: ReplacementCandidateTarget, input: ReplacementCandidateQuantity) {
  return candidateOperation(target, 'quantite', input);
}
export function removeReplacementCandidate(target: ReplacementCandidateTarget, input: ReplacementCandidateContext) {
  return candidateOperation(target, 'annuler', input);
}
export function changeReplacementProduct(target: ReplacementCandidateTarget, input: ReplacementCandidateQuantity & { id_produit_remplacement: number }) {
  return candidateOperation(target, 'changer', input);
}
export function cancelReplacement(id: string, orderId: number) {
  return graphqlJson<ReplacementProposal>(`mutation($id: String!, $orderId: Int!) {
    annulerRemplacement(idJet: $id, idCommande: $orderId) { json }
  }`, { id, orderId }, 'annulerRemplacement');
}
export interface ReplacementBuyerSelection { id_ligne: number; id_produit_remplacement: number }
export async function publicReplacement(token: string, decision?: 'accepted' | 'rejected', selections?: ReplacementBuyerSelection[], ids?: number[]): Promise<ReplacementProposal> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
  const tokenHash = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
  const response = await fetchRest(`${getApiBaseUrl()}/api/remplacements/public/${decision ? 'repondre' : 'consulter'}`, {
    method: decision === 'rejected' ? 'DELETE' : 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(decision === 'accepted' ? { token: tokenHash, selections } : decision === 'rejected' ? { token: tokenHash, ids } : { token: tokenHash }),
    skipAuthRedirect: true, authToken: null, credentials: 'omit', referrerPolicy: 'no-referrer',
  });
  const data = await response.json();
  if (!response.ok) throw new Error(typeof data.message === 'string' ? data.message : 'Impossible de consulter cette proposition');
  if (decision) {
    if (data.success !== true) throw new Error('Réponse de remplacement invalide');
    try { return await publicReplacement(token); }
    catch { throw new Error('Réponse enregistrée. Actualisation impossible : rechargez la page.'); }
  }
  return data;
}

export const replacementStatus: Record<ReplacementProposal['statut'], string> = {
  pending: 'En attente de votre client', accepted: 'Remplacement accepté',
  confirmer: 'Confirmé par un administrateur', rejected: 'Remplacement refusé', expired: 'Proposition expirée', cancelled: 'Proposition annulée',
};
const replacementMoneyFormatter = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });
export function replacementMoney(value: number): string {
  return replacementMoneyFormatter.format(value);
}
